import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import apisauce from "apisauce";

// Helper function to convert image URL to base64
const convertImageToBase64 = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Failed to convert image:", error);
    return null;
  }
};

const VoucherPage = () => {
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voucherData, setVoucherData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [autoDownloadAttempted, setAutoDownloadAttempted] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const voucherRef = useRef(null);

  // Fetch voucher data from backend
  useEffect(() => {
    const fetchVoucherData = async () => {
      try {
        const api = apisauce.create({
          baseURL: process.env.REACT_APP_API_BASE_URL,
          timeout: 30000,
        });

        const response = await api.get(`/api/whatsApp-chatbot/booking/voucher/${bookingId}`);
        console.log(response)
        if (response.ok && response.data?.success) {
          const data = response.data.data;

          // Convert images to base64 for PDF generation
          const [hotelLogoBase64, hotelImgBase64, qrCodeBase64, signatureBase64] = await Promise.all([
            convertImageToBase64(data.hotel_logo),
            convertImageToBase64(data.hotel_img),
            convertImageToBase64(data.payment_qr_code_img),
            convertImageToBase64(data.reservation_signature_img),
          ]);

          setVoucherData({
            ...data,
            hotel_logo_base64: hotelLogoBase64,
            hotel_img_base64: hotelImgBase64,
            payment_qr_code_img_base64: qrCodeBase64,
            reservation_signature_img_base64: signatureBase64,
          });
          setImagesLoaded(true);
        } else {
          setError(response.data?.message || "Failed to fetch voucher data");
        }
      } catch (err) {
        setError("Failed to fetch voucher data");
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      fetchVoucherData();
    }
  }, [bookingId]);

  // Auto-download PDF on initial load (after images are loaded)
  useEffect(() => {
    if (voucherData && imagesLoaded && !autoDownloadAttempted && voucherRef.current) {
      setAutoDownloadAttempted(true);
      // Small delay to ensure content is rendered
      setTimeout(() => {
        handleDownloadPdf();
      }, 1500);
    }
  }, [voucherData, imagesLoaded, autoDownloadAttempted]);

  const handleDownloadPdf = async () => {
    if (!voucherRef.current) return;

    setDownloading(true);
    try {
      // Dynamically import html2pdf
      const html2pdf = (await import("html2pdf.js")).default;

      const element = voucherRef.current;
      const opt = {
        margin: 0,
        filename: `booking-voucher-${bookingId}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { before: ".page-break-before" },
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getUpdatedStatus = (status) => {
    const statusMap = {
      confirmed: "Confirmed",
      pending: "Pending",
      cancelled: "Cancelled",
      checked_in: "Checked In",
      checked_out: "Checked Out",
      no_show: "No Show",
    };
    return statusMap[status] || status || "";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#E6E6FA"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#E6E6FA"
      >
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1">
          Please check the booking ID and try again.
        </Typography>
      </Box>
    );
  }

  const data = voucherData;

  // Build house rules HTML
  let houseRulesHTML = "";
  if (data?.house_rules?.length) {
    const rulesList = data.house_rules.map((rule) => `<li style="color:#777777;">${rule}</li>`).join("");
    houseRulesHTML = `
      <div style="margin-bottom:20px; color:#777777;">
        <p style="color:#777777;"><b>House Rules:</b></p>
        <ul style="margin:0; padding-left:20px; color:#777777;">
          ${rulesList}
        </ul>
      </div>
    `;
  }

  // Build cancellation policy HTML
  let cancellationPolicyHTML = "";
  if (data?.cancellation_policy?.length) {
    const policyList = data.cancellation_policy
      .map((policy) => `<li style="color:#777777;">${policy}</li>`)
      .join("");
    cancellationPolicyHTML = `
      <div style="margin-bottom:20px; color:#777777;">
        <p style="color:#777777;"><b>Cancellation Policy:</b></p>
        <ul style="margin:0; padding-left:20px; color:#777777;">
          ${policyList}
        </ul>
      </div>
    `;
  }

  // Build caution HTML
  let cautionHTML = "";
  if (data?.cautions?.length) {
    const cautionList = data.cautions.map((item) => `<li style="color:#777777;">${item}</li>`).join("");
    cautionHTML = `
      <div style="margin-bottom:20px; color:#777777;">
        <p style="color:#777777;"><b>For Your Safety and Comfort:</b></p>
        <ul style="margin:0; padding-left:20px; color:#777777;">
          ${cautionList}
        </ul>
      </div>
    `;
  }

  // Build bank details HTML
  let bankDetailsHTML = "";
  if (data?.bank_details) {
    const rows = [];

    if (data?.bank_details?.acc_holder_name) {
      rows.push(`
        <tr>
          <td style="padding:5px;width:200px;color:#000000;">Beneficiary Name:</td>
          <td style="padding:5px;color:#000000;">${data?.bank_details?.acc_holder_name}</td>
        </tr>
      `);
    }

    if (data?.bank_details?.acc_no) {
      rows.push(`
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Account No:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.bank_details?.acc_no}</td>
        </tr>
      `);
    }

    if (data?.bank_details?.ifsc) {
      rows.push(`
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">RTGS/NEFT/IFSC:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.bank_details?.ifsc}</td>
        </tr>
      `);
    }

    if (data?.bank_details?.bank_name) {
      rows.push(`
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Bank Name:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.bank_details?.bank_name}</td>
        </tr>
      `);
    }

    if (data?.bank_details?.branch_name) {
      rows.push(`
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Branch:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.bank_details?.branch_name}</td>
        </tr>
      `);
    }

    if (data?.bank_details?.acc_type) {
      rows.push(`
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Account Type:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.bank_details?.acc_type}</td>
        </tr>
      `);
    }

    if (rows.length) {
      bankDetailsHTML = rows.join("");
    }
  }

  // Build rooms HTML
  const roomsHTML = data?.updated_rooms
    ?.map((e) => {
      return `
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Room Type:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${e?.roomtype}</td>
        </tr>
        ${
          e?.total_rooms
            ? `<tr>
                <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Total Rooms:</td>
                <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${e?.total_rooms}</td>
              </tr>`
            : ""
        }
        <tr>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Meal Plan:</td>
          <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${e?.meal_plan}</td>
        </tr>
        ${
          !data?.is_custom_price
            ? `<tr>
                <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Rate:</td>
                <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">INR ${e?.total_amount}</td>
              </tr>`
            : ""
        }
      `;
    })
    .join("");

  const voucherHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <style>
        body, table, td, a, p, div, span, li, ul {
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
          color: #000000 !important;
        }
        table, td {
          mso-table-lspace: 0pt;
          mso-table-rspace: 0pt;
        }
        img {
          -ms-interpolation-mode: bicubic;
          border: 0;
          height: auto;
          line-height: 100%;
          outline: none;
          text-decoration: none;
        }
        table {
          border-collapse: collapse !important;
        }
        body {
          height: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          background-color: #E6E6FA !important;
        }
        h1 {
          color: #ffffff !important;
        }
        .gray-text {
          color: #777777 !important;
        }
      </style>
    </head>
    <body style="margin: 0 !important; padding: 0 !important; background-color: #E6E6FA; color: #000000;">
      <table border="0" cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td align="center" style="background-color: #E6E6FA; padding: 20px;">
            <table align="center" border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width:600px;">
              <tr>
                <td align="center" valign="top" style="font-size:0; padding: 10px; border-radius: 15px 15px 0px 0px; background-color: #9b9b9b;">
                  <div style="display:inline-block; max-width:100%; vertical-align:top; width:100%;">
                    <table align="center" border="0" cellpadding="0" cellspacing="0" width="100%">
                      <tr>
                        <td align="center" valign="top" style="font-family: Open Sans, Helvetica, Arial, sans-serif; font-weight: 800; padding: 5px;">
                          <h1 style="font-size: 20px; font-weight: 800; margin: 0; color: #ffffff; text-transform: uppercase;">
                            Booking Confirmation
                          </h1>
                        </td>
                      </tr>
                    </table>
                  </div>
                </td>
              </tr>

              ${
                data?.hotel_logo_base64 || data?.hotel_logo
                  ? `
                <tr>
                  <td style="padding: 0; margin: 0;">
                    <img src="${data.hotel_logo_base64 || data.hotel_logo}" alt="Hotel Logo" style="width: 100%; height: auto; max-height: 300px; display: block; border: none;" crossorigin="anonymous">
                  </td>
                </tr>
              `
                  : ""
              }

              ${
                data?.hotel_img_base64 || data?.hotel_img
                  ? `
                <tr>
                  <td style="padding: 0; margin: 0;">
                    <img src="${data.hotel_img_base64 || data.hotel_img}" alt="Hotel Image" style="width: 100%; height: auto; max-height: 300px; display: block; border: none;" crossorigin="anonymous">
                  </td>
                </tr>
              `
                  : ""
              }

              <!-- Greeting -->
              <tr>
                <td align="left" style="padding:20px; background-color:#f2f2f2; font-family:Open Sans, Helvetica, Arial, sans-serif; font-size:14px; line-height:20px; color:#000000;">
                  <p style="color:#000000;">Dear <b>${data?.guest_name}</b>,</p>
                  <p style="color:#000000;">Greetings from ${toTitleCase(data?.hotel_name)} !!</p>
                  <p style="color:#000000;">Thank you for choosing ${toTitleCase(data?.hotel_name)} for your upcoming requirement. We are delighted to confirm the availability for your reservation and provide you with the details of your booking. Please review the following information:</p>
                </td>
              </tr>

              <!-- Reservation Details -->
              <tr>
                <td style="padding:20px; background-color:#f2f2f2; color:#000000;">
                  <p style="font-size:small;color:#777777;font-family:Open Sans, Helvetica, Arial, sans-serif;"><b>Reservation Details:</b></p>
                  <div style="background:#fafafa; border-radius:10px; overflow:hidden; font-size:small; font-family:Open Sans, Helvetica, Arial, sans-serif; color:#000000;">
                    <table width="100%" style="border-collapse:collapse;color:#000000;">
                      <tr>
                        <td style="padding:5px;width:200px;color:#000000;">Booking ID:</td>
                        <td style="padding:5px;color:#000000;">${data?.booking_id || ""}</td>
                      </tr>
                      <tr>
                        <td style="padding:5px;width:200px;color:#000000;">Guest Name:</td>
                        <td style="padding:5px;color:#000000;">${data?.guest_name}</td>
                      </tr>
                      <tr>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Check in Date:</td>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.checkin_date}</td>
                      </tr>
                      <tr>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Check out Date:</td>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.checkout_date}</td>
                      </tr>
                      <tr>
                        <td style="padding:5px;border-top:1px solid #ccc; vertical-align:top;color:#000000;">No: of Guests:</td>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">
                          <table width="100%" style="border-collapse:collapse;color:#000000;">
                            <tr>
                              <td style="padding:5px;width:150px;border-bottom:1px solid #ccc;color:#000000;">Adults</td>
                              <td style="padding:5px;border-bottom:1px solid #ccc;color:#000000;">${data?.no_of_adults}</td>
                            </tr>
                            <tr>
                              <td style="padding:5px;color:#000000;">Child</td>
                              <td style="padding:5px;color:#000000;">${data?.no_of_childs}</td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                      ${roomsHTML}
                      <tr>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Extra Mattress:</td>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.extra_mattress ?? 0}</td>
                      </tr>
                      <tr>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Status / Cut-off Date:</td>
                        <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${getUpdatedStatus(data?.status)}${data?.cut_off_date ? ` / ${data?.cut_off_date}` : ""}</td>
                      </tr>
                      ${
                        data?.billing_instruction
                          ? `<tr>
                              <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">Billing Instruction:</td>
                              <td style="padding:5px;border-top:1px solid #ccc;color:#000000;">${data?.billing_instruction}</td>
                            </tr>`
                          : ""
                      }
                    </table>
                  </div>
                </td>
              </tr>

              <!-- Page break before Payment -->
              <tr><td><div class="page-break-before"></div></td></tr>

              <!-- Payment Information -->
              <tr>
                <td style="padding:20px; background-color:#f2f2f2; color:#000000;">
                  <p style="font-size:small;color:#777777;font-family:Open Sans, Helvetica, Arial, sans-serif;">
                    <b>Payment Information:</b>
                  </p>
                  <table width="100%" cellpadding="0" cellspacing="0" style="background:#fafafa;border-radius:10px;font-size:small;font-family:Open Sans, Helvetica, Arial, sans-serif;line-height:1.4;color:#000000;border:1px solid #e0e0e0;">
                    <tr>
                      <td style="padding:10px;color:#000000;">
                        <table width="100%" cellpadding="0" cellspacing="0" style="color:#000000;">
                          <tr>
                            <td style="color:#000000;">
                              <span style="font-weight:600;color:#000000;">Total Amount:</span>
                              <span style="color:#000000;"> INR ${data?.total_price} /-</span>
                            </td>
                          </tr>
                          ${
                            data?.paid_amount
                              ? `<tr>
                                  <td style="color:#000000;">
                                    <span style="font-weight:600;color:#000000;">Paid Amount:</span>
                                    <span style="color:#000000;"> INR ${data?.paid_amount} /-</span>
                                  </td>
                                </tr>`
                              : ""
                          }
                          <tr>
                            <td style="font-weight:600; padding:10px 0 4px 0;color:#000000;">
                              Rate Includes:
                            </td>
                          </tr>
                        </table>
                        <table cellpadding="0" cellspacing="0" width="100%" style="color:#000000;">
                          <tr>
                            <td style="padding:0 0 0 15px;color:#000000;">
                              <ul style="margin:0; padding:0 0 0 15px;color:#000000;">
                                ${data?.meal_plans ? `<li style="color:#000000;">Meal plan details - ${data?.meal_plans}</li>` : ""}
                                <li style="color:#000000;">All taxes</li>
                              </ul>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>

                  ${
                    data?.is_payment_details_available
                      ? `
                    <p style="font-size:small;color:#777777;font-family:Open Sans, Helvetica, Arial, sans-serif;">
                      <b>Payment Method:</b>
                    </p>
                    <p style="margin-top:10px; font-size:small;color:#000000;">
                      We request you to make all payments through NEFT/RTGS for which the details are mentioned below :
                    </p>
                    <div style="background: #fafafa; border-radius: 10px; overflow: hidden; font-size: small; font-family: Open Sans, Helvetica, Arial, sans-serif; width: 100%; border: 1px solid #e0e0e0;">
                      <div style="display: ${data?.payment_qr_code_img ? "flex" : "block"}; align-items: stretch; width: 100%;">
                        <div style="flex: 1; padding: 16px; box-sizing: border-box;">
                          <div style="background-color: #9b9b9b; color: #fff; font-weight: bold; text-align: center; padding: 8px; border-radius: 6px 6px 0 0; margin-bottom: 12px;">
                            Bank Account Details
                          </div>
                          <table style="width: 100%; border-collapse: collapse;">
                            ${bankDetailsHTML}
                          </table>
                        </div>
                        ${
                          data?.payment_qr_code_img_base64 || data?.payment_qr_code_img
                            ? `
                          <div style="width: 320px; padding: 16px; display: flex; align-items: center; justify-content: center; background: #fff; border-left: 1px solid #e0e0e0; box-sizing: border-box;">
                            <img src="${data.payment_qr_code_img_base64 || data.payment_qr_code_img}" alt="QR Code" style="width: 100%; height: auto; object-fit: contain; border-radius: 6px;" crossorigin="anonymous">
                          </div>
                        `
                            : ""
                        }
                      </div>
                    </div>
                    ${
                      data?.payment_link
                        ? `<p style="margin-top:10px;font-size:small;">
                            Or online using our secure payment link:
                            <a href="${data?.payment_link}" target="_blank">${data?.payment_link}</a>
                          </p>`
                        : ""
                    }
                  `
                      : ""
                  }
                </td>
              </tr>

              <!-- Important Info -->
              <tr>
                <td align="left" style="padding:20px; background-color:#f2f2f2; color:#777777; font-size:small; font-family:Open Sans, Helvetica, Arial, sans-serif;">
                  <div style="margin-bottom:20px;color:#777777;">
                    <p style="color:#777777;"><b>Please note the following important information:</b></p>
                    <ul style="margin:0; padding-left:20px;color:#777777;">
                      ${data?.guest_reporting_time ? `<li style="color:#777777;">Reporting time: ${data?.guest_reporting_time || "-"}</li>` : ""}
                      ${data?.guest_checkin_time ? `<li style="color:#777777;">Check in time: ${data?.guest_checkin_time || "-"}</li>` : ""}
                      ${data?.guest_cruise_start_time ? `<li style="color:#777777;">Cruise start time: ${data?.guest_cruise_start_time || "-"}</li>` : ""}
                      ${data?.guest_checkout_time ? `<li style="color:#777777;">Check Out time: ${data?.guest_checkout_time || "-"}</li>` : ""}
                      <li style="color:#777777;">The Tariff is in Indian Rupees.</li>
                    </ul>
                  </div>

                  ${cautionHTML}
                  ${cancellationPolicyHTML}
                  ${houseRulesHTML}

                  <p style="color:#777777;">Should you have any questions or require further assistance, please do not hesitate to contact us at
                    <b>${data?.reservation_mobile || "-"}</b>. We will be happy to assist you.
                  </p>
                  <p style="color:#777777;">We look forward to welcoming you to ${data?.hotel_name} and providing you with a memorable experience.
                    Thank you again for choosing us.</p>
                  <p style="font-style:italic;color:#777777;">Thanks & Regards</p>
                  ${
                    data?.reservation_signature_img_base64 || data?.reservation_signature_img
                      ? `<p style="margin: 0;">
                          <img src="${data.reservation_signature_img_base64 || data.reservation_signature_img}" alt="Signature" style="max-width: 250px; height: auto; display: block;" crossorigin="anonymous">
                        </p>`
                      : ""
                  }
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#E6E6FA",
        position: "relative",
      }}
    >
      {/* Download Button - Fixed at top */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPdf}
          disabled={downloading}
          sx={{
            backgroundColor: "#9b9b9b",
            "&:hover": {
              backgroundColor: "#7a7a7a",
            },
          }}
        >
          {downloading ? "Downloading..." : "Download PDF"}
        </Button>
      </Box>

      {/* Voucher Content */}
      <Box
        ref={voucherRef}
        dangerouslySetInnerHTML={{ __html: voucherHTML }}
        sx={{
          "& table": {
            borderCollapse: "collapse !important",
          },
        }}
      />
    </Box>
  );
};

export default VoucherPage;
